import { c as _c } from "react/compiler-runtime";
import { Alert, Typography, type SxProps } from '@mui/material';
import { Provider, type ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { hasValidConditionValues } from '@/types/advanceFilter';
import type { BackupPolicyEditState } from './BackupPolicyEditState';
import { PolicyNameAndLabels } from './PolicyNameAndLabels';
import { isAwsSourceAccountPolicyType, isPitrPolicyType } from './backupPoliciesTypes';
import { useBackupPolicySupportedProperties } from './backupPolicyProperties';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';
import { useShowsClassificationCadence } from './useShowsClassificationCadence';
const useSourceProviders = policyType => {
  const $ = _c(8);
  const {
    GcpSqlVirtualMachineSupport
  } = useFeatureFlags();
  if (isAwsSourceAccountPolicyType(policyType)) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Aws];
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Azure];
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Aws, Provider.Gcp, Provider.MongoAtlas];
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR) {
    let t0;
    if ($[3] !== GcpSqlVirtualMachineSupport) {
      t0 = GcpSqlVirtualMachineSupport ? [Provider.Azure, Provider.Gcp] : [Provider.Azure];
      $[3] = GcpSqlVirtualMachineSupport;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR) {
    let t0;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Aws];
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR) {
    let t0;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Gcp];
      $[6] = t0;
    } else {
      t0 = $[6];
    }
    return t0;
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR) {
    let t0;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = [Provider.Azure];
      $[7] = t0;
    } else {
      t0 = $[7];
    }
    return t0;
  }
};
export const BackupPolicyConditionEditor = props => {
  const $ = _c(60);
  const nextButtonDisabledTooltip = props.wizardState.resourcesScopeType === "useConditions" && !hasValidConditionValues(props.wizardState.condition) ? "Please create a condition with all required fields" : "Please select at least one resource type";
  const getAvailableResourceTypes = useBackupPolicyAvailableTypes();
  let t0;
  if ($[0] !== getAvailableResourceTypes || $[1] !== props.wizardState.policyType || $[2] !== props.wizardState.vaultStorageTier) {
    t0 = getAvailableResourceTypes(props.wizardState.policyType, props.wizardState.vaultStorageTier);
    $[0] = getAvailableResourceTypes;
    $[1] = props.wizardState.policyType;
    $[2] = props.wizardState.vaultStorageTier;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const supportedResourceTypes = t0;
  const sourceProviders = useSourceProviders(props.wizardState.policyType);
  const selectedResourceTypes = props.wizardState.resourceTypes?.length ? props.wizardState.resourceTypes : supportedResourceTypes;
  const supportedProperties = useBackupPolicySupportedProperties(selectedResourceTypes, sourceProviders, props.wizardState.policyType);
  const showsCadence = useShowsClassificationCadence(props.wizardState.condition, props.wizardState.policyType);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      padding: 0
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props.wizardState.condition || $[6] !== props.wizardState.resourcesScopeType) {
    t2 = () => props.wizardState.resourcesScopeType !== "useConditions" || hasValidConditionValues(props.wizardState.condition);
    $[5] = props.wizardState.condition;
    $[6] = props.wizardState.resourcesScopeType;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props) {
    t3 = async () => {
      props.setNextStep(props.currentStep.next?.[0]);
    };
    $[8] = props;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  let t5;
  if ($[10] !== props) {
    t4 = name => props.setWizardState(state => ({
      ...state,
      name
    }));
    t5 = labels => props.setWizardState(state_0 => ({
      ...state_0,
      labels
    }));
    $[10] = props;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[13] !== props.wizardState.focusOnName || $[14] !== props.wizardState.labels || $[15] !== props.wizardState.name || $[16] !== t4 || $[17] !== t5) {
    t6 = <PolicyNameAndLabels focusOnName={props.wizardState.focusOnName} name={props.wizardState.name} labels={props.wizardState.labels} onNameChange={t4} onLabelsChange={t5} />;
    $[13] = props.wizardState.focusOnName;
    $[14] = props.wizardState.labels;
    $[15] = props.wizardState.name;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== props.wizardState.policyType || $[20] !== props.wizardState.vaultStorageTier) {
    t7 = <BackupPolicySupportedDisclaimer policyType={props.wizardState.policyType} vaultStorageTier={props.wizardState.vaultStorageTier} />;
    $[19] = props.wizardState.policyType;
    $[20] = props.wizardState.vaultStorageTier;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== props) {
    t8 = condition => props.setWizardState({
      ...props.wizardState,
      condition
    });
    $[22] = props;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t10;
  let t9;
  if ($[24] !== props) {
    t9 = includedResources => props.setWizardState({
      ...props.wizardState,
      include: includedResources
    });
    t10 = excludedResources => props.setWizardState({
      ...props.wizardState,
      exclude: excludedResources
    });
    $[24] = props;
    $[25] = t10;
    $[26] = t9;
  } else {
    t10 = $[25];
    t9 = $[26];
  }
  let t11;
  if ($[27] !== props) {
    t11 = resourcesScopeType => props.setWizardState({
      ...props.wizardState,
      resourcesScopeType
    });
    $[27] = props;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      mb: "24px"
    };
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== props.wizardState.policyType || $[31] !== props.wizardState.resourceTypes) {
    t13 = <BackupPolicyConditionTitle resourceTypes={props.wizardState.resourceTypes} policyType={props.wizardState.policyType} sx={t12} />;
    $[30] = props.wizardState.policyType;
    $[31] = props.wizardState.resourceTypes;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too.";
  let t15;
  if ($[33] !== props.wizardState.condition || $[34] !== props.wizardState.exclude || $[35] !== props.wizardState.include || $[36] !== props.wizardState.policyType || $[37] !== props.wizardState.resourceTypes || $[38] !== props.wizardState.resourcesScopeType || $[39] !== selectedResourceTypes || $[40] !== sourceProviders || $[41] !== supportedProperties || $[42] !== t10 || $[43] !== t11 || $[44] !== t13 || $[45] !== t14 || $[46] !== t8 || $[47] !== t9) {
    t15 = <ResourcesSelectionEditor policyType={props.wizardState.policyType} selectedResourceTypes={props.wizardState.resourceTypes} supportedProviders={sourceProviders} supportedResourceTypes={selectedResourceTypes} condition={props.wizardState.condition} setCondition={t8} includedResources={props.wizardState.include} excludedResources={props.wizardState.exclude} setIncludedResources={t9} setExcludedResources={t10} resourcesScopeType={props.wizardState.resourcesScopeType} setResourcesScopeType={t11} supportedProperties={supportedProperties} conditionTitle={t13} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={t14} manualOverridesDescription="Manually include or exclude resources from your backup policy, regardless of dynamic selection." />;
    $[33] = props.wizardState.condition;
    $[34] = props.wizardState.exclude;
    $[35] = props.wizardState.include;
    $[36] = props.wizardState.policyType;
    $[37] = props.wizardState.resourceTypes;
    $[38] = props.wizardState.resourcesScopeType;
    $[39] = selectedResourceTypes;
    $[40] = sourceProviders;
    $[41] = supportedProperties;
    $[42] = t10;
    $[43] = t11;
    $[44] = t13;
    $[45] = t14;
    $[46] = t8;
    $[47] = t9;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== showsCadence) {
    t16 = showsCadence && <Alert severity="info" sx={{
      mt: "24px"
    }}>This policy selects resources by classification results, so a recurring classification will be set — the cadence is determined under Schedules.</Alert>;
    $[49] = showsCadence;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== nextButtonDisabledTooltip || $[52] !== props.stepperLabels || $[53] !== t15 || $[54] !== t16 || $[55] !== t2 || $[56] !== t3 || $[57] !== t6 || $[58] !== t7) {
    t17 = <><StepContainer sx={t1} stepperLabels={props.stepperLabels} stepperIndex={0} nextButtonDisabledTooltip={nextButtonDisabledTooltip} canGoNext={t2} onNextClick={t3} navigationComponent={t6}>{t7}{t15}{t16}</StepContainer></>;
    $[51] = nextButtonDisabledTooltip;
    $[52] = props.stepperLabels;
    $[53] = t15;
    $[54] = t16;
    $[55] = t2;
    $[56] = t3;
    $[57] = t6;
    $[58] = t7;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  return t17;
};
export const BackupPolicyConditionTitle = t0 => {
  const $ = _c(7);
  const {
    resourceTypes,
    policyType,
    sx
  } = t0;
  if (!isPitrPolicyType(policyType) && policyType !== BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    let t1;
    if ($[0] !== sx) {
      t1 = <Typography variant="subtitle1" sx={sx}>Include resources based on these conditions:</Typography>;
      $[0] = sx;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== resourceTypes) {
    t1 = resourceTypes && <span className="font-medium">{resourceTypes?.map(_temp).join(", ")}{" "}</span>;
    $[2] = resourceTypes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== sx || $[5] !== t1) {
    t2 = <Typography variant="subtitle1" sx={sx}>Include{" "}{t1}resources based on these conditions:</Typography>;
    $[4] = sx;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(x) {
  return ResourceTypes[x].displayName;
}